<app-page-breadcrumb [pageTitle]="isEditing ? 'Editar Plantilla de Contenido' : 'Nueva Plantilla de Contenido'"></app-page-breadcrumb>

<div class="space-y-3 mb-4">
  @if (loading) {
  <div class="flex justify-center items-center py-12">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
    <span class="ml-3 text-gray-500 dark:text-gray-400">Cargando plantilla...</span>
  </div>
  }

  @if (error) {
  <div class="p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20">
    <div class="flex items-center gap-3">
      <svg class="w-5 h-5 text-error-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <p class="text-error-600 dark:text-error-400">{{ error }}</p>
    </div>
  </div>
  }
</div>

@if (!loading) {
<form [formGroup]="form" (ngSubmit)="guardar()">
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-5">

    <!-- LEFT COLUMN -->
    <div class="lg:col-span-2 space-y-4">

      <!-- Card: General -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Información General</h2>

        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Nombre <span class="text-error-500">*</span>
          </label>
          <input type="text" formControlName="name" placeholder="Ej. Implementación estándar"
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
            [class.border-error-500]="hasError('name')">
          @if (hasError('name')) {
          <p class="mt-1 text-xs text-error-600">Este campo es requerido (máx. 150 caracteres)</p>
          }
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Descripción</label>
          <textarea formControlName="description" rows="2" placeholder="Paquete base para clientes nuevos..."
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"></textarea>
        </div>
      </div>

      <!-- Card: Items -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ítems Predefinidos</h2>
          <button type="button" (click)="addItem()"
            class="px-2.5 py-1 text-xs rounded-lg font-medium transition-colors bg-brand-500 text-white hover:bg-brand-600 flex items-center gap-1">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
            </svg>
            Agregar ítem
          </button>
        </div>

        @if (items.length === 0) {
        <p class="text-sm text-gray-400 dark:text-gray-600 py-4 text-center">
          Aún no hay ítems. Agrega productos o servicios que se clonarán al generar una cotización.
        </p>
        }

        <div class="space-y-3" formArrayName="items">
          @for (item of items.controls; track $index; let i = $index) {
          <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-3" [formGroupName]="i">
            <div class="flex items-start justify-between gap-2">
              <span class="text-xs font-medium text-gray-400 dark:text-gray-500 mt-2">#{{ i + 1 }}</span>
              <button type="button" (click)="removeItem(i)"
                class="rounded-lg p-1.5 text-error-500 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors" title="Quitar ítem">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            <!-- Tipo de la línea: mismo patrón que el paso 2 del wizard de cotización. -->
            <div class="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit">
              <button type="button" (click)="setItemType(i, 'product')"
                [class]="item.get('itemType')?.value === 'product' ? 'px-3 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm' : 'px-3 py-1 text-xs text-gray-600 dark:text-gray-400'">
                Productos
              </button>
              <button type="button" (click)="selectServiceType(i)"
                [class]="isServiceType(item.get('itemType')?.value) ? 'px-3 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm' : 'px-3 py-1 text-xs text-gray-600 dark:text-gray-400'">
                Servicios
              </button>
              <button type="button" (click)="setItemType(i, 'custom')"
                [class]="item.get('itemType')?.value === 'custom' ? 'px-3 py-1 text-xs font-medium rounded bg-white dark:bg-gray-700 text-brand-600 dark:text-brand-400 shadow-sm' : 'px-3 py-1 text-xs text-gray-600 dark:text-gray-400'">
                Personalizado
              </button>
            </div>

            <!-- Subtipo de Productos -->
            @if (item.get('itemType')?.value === 'product') {
            <div class="flex gap-4">
              <label class="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" [name]="'tplKind' + i" [checked]="productKindFor(i) === 'inventory'"
                  (change)="setProductKindFor(i, 'inventory')" class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                <span class="text-xs text-gray-700 dark:text-gray-300">Con Inventario</span>
              </label>
              <label class="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" [name]="'tplKind' + i" [checked]="productKindFor(i) === 'no_inventory'"
                  (change)="setProductKindFor(i, 'no_inventory')" class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                <span class="text-xs text-gray-700 dark:text-gray-300">Sin Inventario</span>
              </label>
            </div>
            }

            <!-- Subtipo de Servicios -->
            @if (isServiceType(item.get('itemType')?.value)) {
            <div class="flex gap-4">
              <label class="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" [name]="'tplSub' + i" [checked]="item.get('itemType')?.value === 'service_resource'"
                  (change)="setItemType(i, 'service_resource')" class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                <span class="text-xs text-gray-700 dark:text-gray-300">Recursos</span>
              </label>
              <label class="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" [name]="'tplSub' + i" [checked]="item.get('itemType')?.value === 'service_consulting'"
                  (change)="setItemType(i, 'service_consulting')" class="w-3.5 h-3.5 text-brand-600 focus:ring-brand-500">
                <span class="text-xs text-gray-700 dark:text-gray-300">Consultoría</span>
              </label>
            </div>
            }

            <!-- Buscador del catálogo: autocompleta nombre, descripción, precio y unidad. -->
            @if (isProductLikeType(item.get('itemType')?.value)) {
            <div class="relative">
              <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {{ item.get('itemType')?.value === 'product' ? 'Producto del catálogo' : 'Servicio del catálogo (opcional)' }}
              </label>
              <div class="flex gap-2">
                <input type="text"
                  [value]="itemProductSearchTerms[i] || ''"
                  (input)="onProductSearchInput(i, $any($event.target).value)"
                  (keydown.enter)="onProductSearchEnter(i, $event)"
                  (blur)="closeProductDropdown(i)"
                  placeholder="Buscar en el catálogo... (Enter para ver todos)"
                  class="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                @if (item.get('productId')?.value) {
                <button type="button" (click)="clearProductForItem(i)"
                  class="px-3 py-2 text-xs rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  title="Desvincular del catálogo y dejar la línea libre">Desvincular</button>
                }
              </div>
              @if (showItemProductDropdown[i] && filteredProductsFor(i).length > 0) {
              <div class="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900 max-h-40 overflow-y-auto">
                @for (p of filteredProductsFor(i); track p.id) {
                <button type="button" (mousedown)="selectProductForItem(i, p)"
                  class="w-full px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span class="block text-sm font-medium text-gray-900 dark:text-white">{{ p.name }}</span>
                  <span class="block text-xs text-gray-500 dark:text-gray-400">{{ p.sku }}</span>
                </button>
                }
              </div>
              }
              @if (item.get('productId')?.value) {
              <p class="mt-1 text-[11px] text-success-600 dark:text-success-400">
                Ligado al catálogo: la cotización generada arrastra su código y categoría.
              </p>
              } @else {
              <p class="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                Sin elegir nada, la línea funciona igual que "Personalizado": se guarda tal cual la escribas.
              </p>
              }
            </div>
            }

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Nombre del ítem <span class="text-error-500">*</span>
                </label>
                <input type="text" formControlName="itemName" placeholder="Ej. Implementación CRM"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  [class.border-error-500]="itemHasError(i, 'itemName')">
              </div>

              <div class="sm:col-span-2">
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Descripción</label>
                <input type="text" formControlName="description" placeholder="Configuración inicial..."
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Cantidad <span class="text-error-500">*</span>
                </label>
                <input type="number" formControlName="quantity" min="0.01" step="0.01"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  [class.border-error-500]="itemHasError(i, 'quantity')">
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Precio unitario <span class="text-error-500">*</span>
                </label>
                <input type="number" formControlName="unitPrice" min="0" step="0.01"
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  [class.border-error-500]="itemHasError(i, 'unitPrice')">
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Unidad</label>
                <input type="text" formControlName="unit" placeholder="pcs, hrs..."
                  class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent">
              </div>
            </div>
          </div>
          }
        </div>
      </div>

      <!-- Card: Defaults -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Términos y Notas Predeterminados</h2>

        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Términos y condiciones</label>
          <textarea formControlName="defaultTermsConditions" rows="4"
            placeholder="Términos por defecto que se incluirán en las cotizaciones generadas..."
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"></textarea>
        </div>

        <div>
          <label class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notas</label>
          <textarea formControlName="defaultNotes" rows="3"
            placeholder="Notas por defecto..."
            class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-y"></textarea>
        </div>
      </div>

    </div>

    <!-- RIGHT COLUMN -->
    <div class="space-y-4">

      <!-- Card: Options -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-4">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Opciones</h2>

        <div class="space-y-3">
          <div>
            <label class="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" formControlName="approvalRequired"
                class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500">
              <span class="text-sm text-gray-700 dark:text-gray-300">Requiere aprobación interna</span>
            </label>
            <p class="mt-1 ml-7 text-xs text-gray-500 dark:text-gray-400">
              Las cotizaciones generadas desde esta plantilla deberán aprobarse antes de poder publicarse o enviarse.
            </p>
          </div>

          <label class="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" formControlName="isActive"
              class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-brand-500 focus:ring-brand-500">
            <span class="text-sm text-gray-700 dark:text-gray-300">Plantilla activa</span>
          </label>
        </div>
      </div>

      <!-- Card: Actions -->
      <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3">
        <h2 class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Acciones</h2>

        <button type="submit" [disabled]="saving"
          class="w-full px-4 py-2 text-sm font-medium rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
          @if (saving) {
          <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          Guardando...
          } @else {
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          {{ isEditing ? 'Actualizar Plantilla' : 'Crear Plantilla' }}
          }
        </button>

        <button type="button" (click)="cancelar()" [disabled]="saving"
          class="w-full px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          Cancelar
        </button>
      </div>

    </div>
  </div>
</form>
}
